import { useMemo, useState } from "react";

const professionIconsContext = require.context(
  "../images/GW2Icons/professions",
  false,
  /\.(png|jpe?g|gif|webp|svg)$/,
);

const raceIconsContext = require.context(
  "../images/GW2Icons/races",
  false,
  /\.(png|jpe?g|gif|webp|svg)$/,
);

const parseNameFromFileName = (fileName) => {
  const withoutExtension = String(fileName || "").replace(/\.[^.]+$/, "");
  return withoutExtension.split("_")[0] || withoutExtension;
};

const createOptions = (context, categoryKey) => {
  const uniqueByName = new Map();

  context.keys().forEach((filePath) => {
    const fileName = filePath.replace("./", "");
    const name = parseNameFromFileName(fileName);
    const normalizedName = name.toLowerCase();

    if (uniqueByName.has(normalizedName)) return;

    uniqueByName.set(normalizedName, {
      id: `${categoryKey}-${normalizedName}`,
      name,
      iconSrc: context(filePath),
      category: categoryKey,
    });
  });

  return Array.from(uniqueByName.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const PROFESSION_OPTIONS = createOptions(professionIconsContext, "profession");
const RACE_OPTIONS = createOptions(raceIconsContext, "race");

const createDefaultCheckedState = (options) =>
  options.reduce((acc, option) => {
    acc[option.id] = true;
    return acc;
  }, {});

const createUniformCheckedState = (options, checked) =>
  options.reduce((acc, option) => {
    acc[option.id] = checked;
    return acc;
  }, {});

function MissionSetupWindow({ visible, onClose, onPopulate, disabled }) {
  const [professionFilter, setProfessionFilter] = useState("");
  const [raceFilter, setRaceFilter] = useState("");
  const [checkedProfessions, setCheckedProfessions] = useState(() => createDefaultCheckedState(PROFESSION_OPTIONS));
  const [checkedRaces, setCheckedRaces] = useState(() => createDefaultCheckedState(RACE_OPTIONS));
  const [iconMode, setIconMode] = useState("icons");

  const filteredProfessions = useMemo(() => {
    const normalizedFilter = professionFilter.trim().toLowerCase();
    if (!normalizedFilter) return PROFESSION_OPTIONS;
    return PROFESSION_OPTIONS.filter((option) => option.name.toLowerCase().includes(normalizedFilter));
  }, [professionFilter]);

  const filteredRaces = useMemo(() => {
    const normalizedFilter = raceFilter.trim().toLowerCase();
    if (!normalizedFilter) return RACE_OPTIONS;
    return RACE_OPTIONS.filter((option) => option.name.toLowerCase().includes(normalizedFilter));
  }, [raceFilter]);

  const selectedEntries = useMemo(() => {
    const selectedProfessions = PROFESSION_OPTIONS.filter((option) => checkedProfessions[option.id]);
    const selectedRaces = RACE_OPTIONS.filter((option) => checkedRaces[option.id]);
    return [...selectedProfessions, ...selectedRaces];
  }, [checkedProfessions, checkedRaces]);

  const handlePopulate = () => {
    if (!selectedEntries.length || disabled) return;

    onPopulate?.({
      entries: selectedEntries,
      useQuagganEmojis: iconMode === "quaggan",
    });
    onClose?.();
  };

  const handleToggleAll = (type, checked) => {
    if (type === "profession") {
      setCheckedProfessions(createUniformCheckedState(PROFESSION_OPTIONS, checked));
      return;
    }

    setCheckedRaces(createUniformCheckedState(RACE_OPTIONS, checked));
  };

  if (!visible) return null;

  return (
    <div className="mission-setup-overlay" onClick={onClose}>
      <div className="mission-setup-modal" onClick={(event) => event.stopPropagation()}>
        <div className="mission-setup-header-row">
          <div>
            <h2 className="mission-setup-title">Guild Mission Setup</h2>
            <p className="mission-setup-help">Populate the wheel with Professions and or Races</p>
          </div>
          <button
            type="button"
            className="mission-setup-close-btn"
            onClick={onClose}
            aria-label="Close mission setup"
          >
            &times;
          </button>
        </div>

        <div className="mission-setup-columns">
          <div className="mission-setup-column">
            <div className="mission-setup-column-header">
              <h3 className="mission-setup-column-title">Professions</h3>
              <label className="mission-setup-toggle-all">
                <input
                  type="checkbox"
                  checked={PROFESSION_OPTIONS.every((option) => checkedProfessions[option.id])}
                  onChange={(event) => handleToggleAll("profession", event.target.checked)}
                  disabled={disabled}
                />
                All
              </label>
            </div>
            <input
              type="text"
              className="login-input"
              placeholder="Filter professions"
              value={professionFilter}
              onChange={(event) => setProfessionFilter(event.target.value)}
              disabled={disabled}
            />
            <div className="mission-setup-list" role="group" aria-label="Profession options">
              {filteredProfessions.map((option) => (
                <label key={option.id} className="mission-setup-option-row">
                  <input
                    type="checkbox"
                    checked={Boolean(checkedProfessions[option.id])}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setCheckedProfessions((previous) => ({
                        ...previous,
                        [option.id]: checked,
                      }));
                    }}
                    disabled={disabled}
                  />
                  <img src={option.iconSrc} alt={`${option.name} icon`} className="mission-setup-option-icon" />
                  <span>{option.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mission-setup-column">
            <div className="mission-setup-column-header">
              <h3 className="mission-setup-column-title">Races</h3>
              <label className="mission-setup-toggle-all">
                <input
                  type="checkbox"
                  checked={RACE_OPTIONS.every((option) => checkedRaces[option.id])}
                  onChange={(event) => handleToggleAll("race", event.target.checked)}
                  disabled={disabled}
                />
                All
              </label>
            </div>
            <input
              type="text"
              className="login-input"
              placeholder="Filter races"
              value={raceFilter}
              onChange={(event) => setRaceFilter(event.target.value)}
              disabled={disabled}
            />
            <div className="mission-setup-list" role="group" aria-label="Race options">
              {filteredRaces.map((option) => (
                <label key={option.id} className="mission-setup-option-row">
                  <input
                    type="checkbox"
                    checked={Boolean(checkedRaces[option.id])}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setCheckedRaces((previous) => ({
                        ...previous,
                        [option.id]: checked,
                      }));
                    }}
                    disabled={disabled}
                  />
                  <img src={option.iconSrc} alt={`${option.name} icon`} className="mission-setup-option-icon" />
                  <span>{option.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mission-setup-footer">
          <div className="mission-setup-radio-group" role="radiogroup" aria-label="Mission icon mode">
            <label className="mission-setup-radio-option">
              <input
                type="radio"
                name="mission-icon-mode"
                value="icons"
                checked={iconMode === "icons"}
                onChange={() => setIconMode("icons")}
                disabled={disabled}
              />
              Use GW2 icons
            </label>
            <label className="mission-setup-radio-option">
              <input
                type="radio"
                name="mission-icon-mode"
                value="quaggan"
                checked={iconMode === "quaggan"}
                onChange={() => setIconMode("quaggan")}
                disabled={disabled}
              />
              Use random Quaggan emojis
            </label>
          </div>

          <button
            type="button"
            className="names-btn primary"
            onClick={handlePopulate}
            disabled={disabled || selectedEntries.length === 0}
          >
            Populate wheel
          </button>
        </div>
      </div>
    </div>
  );
}

export default MissionSetupWindow;